import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";
import { localBusinessSchema } from "../../config/structuredData";

interface SEOProps {
  title: string;
  description: string;
  /** Optional override. When omitted, canonical is derived from the route. */
  canonical?: string;
  lang?: string;
  ogImage?: string;
  schema?: object[];
}

const BASE = "https://creationarts.com";
const SUPPORTED = ["en", "ar"];

const pickLang = (...candidates: (string | undefined)[]): string => {
  for (const c of candidates) {
    const v = (c ?? "").toLowerCase();
    if (SUPPORTED.includes(v)) return v;
  }
  return "en";
};

const SEO = ({
  title,
  description,
  canonical,
  lang: langProp,
  ogImage = `${BASE}/creation-arts-flags-banner.png`,
  schema,
}: SEOProps) => {
  const { pathname } = useLocation();
  const { lang: langParam } = useParams<{ lang: string }>();

  // Language source of truth: the /:lang URL segment.
  const segLang = pathname.split("/")[1];
  const lang = pickLang(langParam, langProp, segLang);

  // Normalize: drop trailing slash (except root) for a stable canonical.
  const cleanPath =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  // Path with the leading /:lang segment removed, e.g. "/services/custom-flags".
  const pathNoLang = cleanPath.replace(/^\/(en|ar)(?=\/|$)/, "");

  const enUrl = `${BASE}/en${pathNoLang}`;
  const arUrl = `${BASE}/ar${pathNoLang}`;
  const canonicalUrl = canonical ?? `${BASE}/${lang}${pathNoLang}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — each points to its own language URL */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Creation Arts" />
      <meta property="og:locale" content={lang === "ar" ? "ar_AE" : "en_US"} />
      <meta
        property="og:locale:alternate"
        content={lang === "ar" ? "en_US" : "ar_AE"}
      />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {[localBusinessSchema(), ...(schema ?? [])].map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
